/* eslint-disable import/no-cycle, @typescript-eslint/tslint/config */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {
  GetCategoryInitialDataQuery,
  GetCategoryInitialDataQueryVariables,
  GetCategoryProductsQuery,
  GetCategoryProductsQueryVariables,
  GetDataQuery,
  GetFilteredProductsQuery,
  GetFilteredProductsQueryVariables,
  GetInitialDataForExternalSourceQuery,
  GetProductItemsByProductIdQuery,
  GetRelatedProductsByAlgorithmQuery,
  GetRelatedProductsByAlgorithmQueryVariables,
  ProductFilters,
  ProductSort,
} from '../graphql/queries-schema';
import {Topology} from '@wix/wixstores-client-core/dist/es/src/constants';
import type {IGetInitialData, IOldGetInitialData} from '../types/galleryTypes';
import {query as getSliderGalleryDataQueryString} from '../graphql/getSliderGalleryData.graphql';
import {query as getDataQueryString} from '../graphql/getData.graphql';
import {query as getCategoryProductsQueryString} from '../graphql/getCategoryProducts.graphql';
import {query as getFilteredProductsQueryString} from '../graphql/getFilteredProducts.graphql';
import {query as getRelatedProductsByAlgorithmString} from '../graphql/getRelatedProductsByAlgorithm.graphql';
import {query as getCategoryInitialDataQueryString} from '../graphql/getCategoryInitialData.graphql';
import {query as getProductItemsByProductIdQueryString} from '../graphql/getProductItemsByProductId.graphql';
import {query as getProductItemsByProductIdWithoutVariantDiscountQueryString} from '../graphql/getProductItemsByProductIdWithoutVariantDiscount.graphql';

import {query as getSliderGalleryDataWithUnusedFieldsQueryString} from '../graphql/withUnusedFields/getSliderGalleryDataWithUnusedFields.graphql';
import {query as getDataWithUnusedFieldsQueryString} from '../graphql/withUnusedFields/getDataWithUnusedFields.graphql';
import {query as getCategoryProductsWithUnusedFieldsQueryString} from '../graphql/withUnusedFields/getCategoryProductsWithUnusedFields.graphql';
import {query as getFilteredProductsWithUnusedFieldsQueryString} from '../graphql/withUnusedFields/getFilteredProductsWithUnusedFields.graphql';
import {query as getRelatedProductsByAlgorithmWithUnusedFieldsString} from '../graphql/withUnusedFields/getRelatedProductsByAlgorithmWithUnusedFields.graphql';

import {query as getSliderGalleryDataWithoutDiscountsQueryString} from '../graphql/withoutDiscounts/getSliderGalleryDataWithoutDiscounts.graphql';
import {query as getDataWithoutDiscountsQueryString} from '../graphql/withoutDiscounts/getDataWithoutDiscountsWith.graphql';
import {query as getCategoryProductsWithoutDiscountsQueryString} from '../graphql/withoutDiscounts/getCategoryProductsWithoutDiscounts.graphql';
import {query as getFilteredProductsWithoutDiscountsQueryString} from '../graphql/withoutDiscounts/getFilteredProductsWithoutDiscounts.graphql';
import {query as getRelatedProductsByAlgorithmWithoutDiscountsString} from '../graphql/withoutDiscounts/getRelatedProductsByAlgorithmWithoutDiscounts.graphql';

import {query as getSliderGalleryDataWithoutDiscountsWithUnusedFieldsQueryString} from '../graphql/withoutDiscountsWithUnusedFields/getSliderGalleryDataWithoutDiscountsWithUnusedFields.graphql';
import {query as getDataWithoutDiscountsWithUnusedFieldsQueryString} from '../graphql/withoutDiscountsWithUnusedFields/getDataWithoutDiscountsWithUnusedFields.graphql';
import {query as getCategoryProductsWithoutDiscountsWithUnusedFieldsQueryString} from '../graphql/withoutDiscountsWithUnusedFields/getCategoryProductsWithoutDiscountsWithUnusedFields.graphql';
import {query as getFilteredProductsWithoutDiscountsWithUnusedFieldsQueryString} from '../graphql/withoutDiscountsWithUnusedFields/getFilteredProductsWithoutDiscountsWithUnusedFields.graphql';
import {query as getRelatedProductsByAlgorithmWithoutDiscountsWithUnusedFieldsString} from '../graphql/withoutDiscountsWithUnusedFields/getRelatedProductsByAlgorithmWithoutDiscountsWithUnusedFields.graphql';

import _ from 'lodash';
import {DEFAULT_COLLECTION_ID, MAX_PRODUCTS, Experiments, FedopsInteraction} from '../constants';
import {query as getInitialDataForExternalSource} from '../graphql/getInitialDataForExternalSource.graphql';
import {Filters} from '../types/filters';
import {mapISortingToIProductSortDto, mapISortingToSiteSearchSortDto} from '../services/sort/iSortingToDtoMapper';
import {ISorting, Sorting} from '../types/sorting';
import {ControllerFlowAPI} from '@wix/yoshi-flow-editor';

export interface GetProductsResponse {
  list: GetFilteredProductsQuery['catalog']['category']['productsWithMetaData']['list'];
  totalCount: number;
}

export interface GetProductsRequest {
  collectionId: string;
  offset: number;
  limit: number;
  withOptions: boolean;
  withPriceRange: boolean;
  filters?: Filters;
  sorting?: Sorting;
}

export class DataApi {
  private externalDataSourceGetProducts: (GetProductsRequest) => Promise<GetProductsResponse>;

  constructor(
    private readonly siteStore: SiteStore,
    private readonly panoramaClient: ControllerFlowAPI['panoramaClient'],
    private readonly consumerName: string
  ) {
    //
  }

  public setExternalDataSourceGetProducts(val: (req: GetProductsRequest) => Promise<GetProductsResponse>) {
    this.externalDataSourceGetProducts = val;
  }

  public oldGetInitialData({externalId, compId, limit, withPriceRange, withOptions}: IOldGetInitialData): Promise<{
    data: GetDataQuery;
  }> {
    /* istanbul ignore next */
    const {query: queryString, operationName} = this.getQueryByExperiments({
      default: {query: getSliderGalleryDataQueryString, operationName: 'getSliderGalleryData'},
      withoutDiscounts: {
        query: getSliderGalleryDataWithoutDiscountsQueryString,
        operationName: 'getSliderGalleryDataWithoutDiscounts',
      },
      withUnusedFields: {
        query: getSliderGalleryDataWithUnusedFieldsQueryString,
        operationName: 'getSliderGalleryDataWithUnusedFields',
      },
      withoutDiscountsWithUnusedFields: {
        query: getSliderGalleryDataWithoutDiscountsWithUnusedFieldsQueryString,
        operationName: 'getSliderGalleryDataWithoutDiscountsWithUnusedFields',
      },
    });

    const data: any = {
      query: queryString,
      source: 'WixStoresWebClient',
      operationName,
      variables: {externalId: externalId || '', compId, limit, withPriceRange, withOptions},
    };

    return this.sendRequest(data) as Promise<{
      data: GetDataQuery;
    }>;
  }

  public getInitialData({
    externalId,
    compId,
    limit,
    sort,
    filters,
    offset,
    withOptions,
    withPriceRange,
    mainCollectionId,
  }: IGetInitialData): Promise<{
    data: GetDataQuery;
  }> {
    const maxLimit = Math.min(limit, MAX_PRODUCTS);
    /* istanbul ignore next */
    const {query: queryString, operationName} = this.getQueryByExperiments({
      default: {query: getDataQueryString, operationName: 'getData'},
      withoutDiscounts: {query: getDataWithoutDiscountsQueryString, operationName: 'getDataWithoutDiscounts'},
      withUnusedFields: {query: getDataWithUnusedFieldsQueryString, operationName: 'getDataWithUnusedFields'},
      withoutDiscountsWithUnusedFields: {
        query: getDataWithoutDiscountsWithUnusedFieldsQueryString,
        operationName: 'getDataWithoutDiscountsWithUnusedFields',
      },
    });

    const data = {
      query: queryString,
      source: 'WixStoresWebClient',
      operationName,
      variables: {
        externalId: externalId || '',
        compId: mainCollectionId ? undefined : compId,
        limit: maxLimit,
        sort,
        filters,
        offset,
        withOptions,
        withPriceRange,
        mainCollectionId,
      },
    };

    return this.sendRequest(data) as Promise<{
      data: GetDataQuery;
    }>;
  }

  public getRelatedProductsByAlgorithm({
    externalId = '',
    productIds,
    algorithmId,
    appId,
    withOptions,
    withPriceRange,
  }: {
    externalId: string;
    productIds: string[];
    algorithmId: string;
    appId: string;
    withOptions: boolean;
    withPriceRange: boolean;
  }): Promise<{data: GetRelatedProductsByAlgorithmQuery}> {
    const queryVariables: GetRelatedProductsByAlgorithmQueryVariables = {
      externalId,
      productIds,
      algorithm: {id: algorithmId, appId},
      withOptions,
      withPriceRange,
    };

    /* istanbul ignore next */
    const {query: queryString, operationName} = this.getQueryByExperiments({
      default: {query: getRelatedProductsByAlgorithmString, operationName: 'getRelatedProductsByAlgorithm'},
      withoutDiscounts: {
        query: getRelatedProductsByAlgorithmWithoutDiscountsString,
        operationName: 'getRelatedProductsByAlgorithmWithoutDiscounts',
      },
      withUnusedFields: {
        query: getRelatedProductsByAlgorithmWithUnusedFieldsString,
        operationName: 'getRelatedProductsByAlgorithmWithUnusedFields',
      },
      withoutDiscountsWithUnusedFields: {
        query: getRelatedProductsByAlgorithmWithoutDiscountsWithUnusedFieldsString,
        operationName: 'getRelatedProductsByAlgorithmWithoutDiscountsWithUnusedFields',
      },
    });

    const data: any = {
      query: queryString,
      source: 'WixStoresWebClient',
      operationName,
      variables: queryVariables,
    };
    return this.sendRequest(data) as Promise<{data: GetRelatedProductsByAlgorithmQuery}>;
  }

  /* istanbul ignore next */
  private getQueryByExperiments<T>(variants: {
    default: T;
    withoutDiscounts: T;
    withUnusedFields: T;
    withoutDiscountsWithUnusedFields: T;
  }): T {
    const removeUnused = this.siteStore.experiments.enabled(Experiments.GalleryRemoveUnusedFieldsFromQuery);
    const addDiscounts = this.siteStore.experiments.enabled(Experiments.AddDiscountsToVariantsItemsQueriesGallery);

    if (addDiscounts && removeUnused) {
      return variants.default;
    } else if (!addDiscounts && removeUnused) {
      return variants.withoutDiscounts;
    } else if (addDiscounts && !removeUnused) {
      return variants.withUnusedFields;
    }
    return variants.withoutDiscountsWithUnusedFields;
  }

  private sendRequest(data: any): Promise<any> {
    return this.siteStore.tryGetGqlAndFallbackToPost(
      this.siteStore.resolveAbsoluteUrl(`/${Topology.STOREFRONT_GRAPHQL_URL}`),
      data
    );
  }

  public async getCategoryProducts(
    variables: GetCategoryProductsQueryVariables
  ): Promise<{data: GetCategoryProductsQuery}> {
    /* istanbul ignore next */
    const {query: queryString, operationName} = this.getQueryByExperiments({
      default: {query: getCategoryProductsQueryString, operationName: 'getCategoryProducts'},
      withoutDiscounts: {
        query: getCategoryProductsWithoutDiscountsQueryString,
        operationName: 'getCategoryProductsWithoutDiscounts',
      },
      withUnusedFields: {
        query: getCategoryProductsWithUnusedFieldsQueryString,
        operationName: 'getCategoryProductsWithUnusedFields',
      },
      withoutDiscountsWithUnusedFields: {
        query: getCategoryProductsWithoutDiscountsWithUnusedFieldsQueryString,
        operationName: 'getCategoryProductsWithoutDiscountsWithUnusedFields',
      },
    });

    const data: any = {
      query: queryString,
      source: 'WixStoresWebClient',
      operationName,
      variables,
    };

    return this.sendRequest(data);
  }

  public async getProductsByOffset(request: GetProductsRequest) {
    const {collectionId, offset, limit, filters, withOptions, withPriceRange, sorting} = request;
    if (this.externalDataSourceGetProducts) {
      return this.externalDataSourceGetProducts({
        ...request,
        sorting: mapISortingToSiteSearchSortDto(sorting as ISorting),
      });
    }

    const response = await this.getProductsByGraphQL(
      collectionId,
      offset,
      limit,
      filters as ProductFilters,
      withOptions,
      withPriceRange,
      sorting as ISorting
    );
    const {list, totalCount} = response.data.catalog.category.productsWithMetaData;
    return {list: _.compact(list), totalCount};
  }

  public async getProducts({
    collectionId,
    filters,
    fromIndex,
    sorting,
    toIndex,
    withOptions,
    withPriceRange,
  }: {
    collectionId: string;
    fromIndex: number;
    toIndex: number;
    withOptions: boolean;
    withPriceRange: boolean;
    filters?: Filters;
    sorting?: ISorting;
  }): Promise<GetProductsResponse> {
    const offset = fromIndex;
    const limit = toIndex - fromIndex;
    return this.getProductsByOffset({
      collectionId,
      filters,
      sorting,
      withOptions,
      withPriceRange,
      offset,
      limit,
    });
  }

  public async getProductsByGraphQL(
    mainCollectionId: string,
    offset: number,
    limit: number,
    filters: ProductFilters | null,
    withOptions: boolean,
    withPriceRange: boolean,
    sorting?: ISorting
  ): Promise<{data: GetFilteredProductsQuery}> {
    const sort = mapISortingToIProductSortDto(sorting) as ProductSort;

    const variables: GetFilteredProductsQueryVariables = {
      mainCollectionId,
      offset,
      limit,
      sort,
      filters,
      withOptions,
      withPriceRange,
    };

    /* istanbul ignore next */
    const {query: queryString, operationName} = this.getQueryByExperiments({
      default: {query: getFilteredProductsQueryString, operationName: 'getFilteredProducts'},
      withoutDiscounts: {
        query: getFilteredProductsWithoutDiscountsQueryString,
        operationName: 'getFilteredProductsWithoutDiscounts',
      },
      withUnusedFields: {
        query: getFilteredProductsWithUnusedFieldsQueryString,
        operationName: 'getFilteredProductsWithUnusedFields',
      },
      withoutDiscountsWithUnusedFields: {
        query: getFilteredProductsWithoutDiscountsWithUnusedFieldsQueryString,
        operationName: 'getFilteredProductsWithoutDiscountsWithUnusedFields',
      },
    });

    const data = {
      variables,
      query: queryString,
      source: 'WixStoresWebClient',
      operationName,
    };

    this.panoramaClient.transaction(FedopsInteraction.GetFilteredProducts).start({componentType: this.consumerName});

    const response = await this.sendRequest(data);

    this.panoramaClient.transaction(FedopsInteraction.GetFilteredProducts).finish({
      componentType: this.consumerName,
      offset,
      limit,
    });

    return response;
  }

  public async getCategoryInitialData({
    externalId,
    currentCategoryId,
    withRichContentDescription,
  }: GetCategoryInitialDataQueryVariables): Promise<{data: GetCategoryInitialDataQuery}> {
    const data = {
      variables: {
        externalId: externalId || /* istanbul ignore next */ '',
        currentCategoryId: currentCategoryId || DEFAULT_COLLECTION_ID,
        withRichContentDescription,
      },
      query: getCategoryInitialDataQueryString,
      source: 'WixStoresWebClient',
      operationName: 'getCategoryInitialData',
    };

    return this.sendRequest(data);
  }

  public async getInitialDataForExternalSource(
    externalId: string
  ): Promise<{data: GetInitialDataForExternalSourceQuery}> {
    const data: any = {
      query: getInitialDataForExternalSource,
      source: 'WixStoresWebClient',
      operationName: 'getInitialDataForExternalSource',
      variables: {externalId: externalId || /* istanbul ignore next */ ''},
    };

    return this.sendRequest(data);
  }

  public async getProductItemsByProductId(productId: string): Promise<{data: GetProductItemsByProductIdQuery}> {
    /* istanbul ignore next */
    const queryString = this.siteStore.experiments.enabled(Experiments.AddDiscountsToVariantsItemsQueriesGallery)
      ? getProductItemsByProductIdQueryString
      : getProductItemsByProductIdWithoutVariantDiscountQueryString;

    const data = {
      variables: {productId},
      query: queryString,
      source: 'WixStoresWeb',
      operationName:
        /* istanbul ignore next */
        queryString === getProductItemsByProductIdQueryString
          ? 'getProductItemsByProductId'
          : 'getProductItemsByProductIdWithoutVariantDiscount',
    };
    return this.sendRequest(data);
  }
}
